import { useState } from 'react';
import type { ReactNode } from 'react';
import type { CheckoutSession, CheckoutState } from '../types/checkout';
import { buildUPIAppLinks, type UPIAppLink } from '../lib/upi-deep-links';
import { CryptoTransferCheckout } from './CryptoTransferCheckout';
import phonePeLogo from '../assets/payment-methods/phonepe.png';
import googlePayLogo from '../assets/payment-methods/google_pay.png';
import paytmLogo from '../assets/payment-methods/paytm.png';
import upiLogo from '../assets/payment-methods/upi.png';

interface CheckoutStatesProps {
  state: CheckoutState;
  session: CheckoutSession | null;
  error: string | null;
  upiIntent?: string | null;
  onPay: (targetUrl?: string) => void | Promise<void>;
  onSimulatePayment?: (status: 'SUCCESS' | 'FAILED') => void | Promise<void>;
  onReturnToMerchant: () => void;
  onCancel?: () => void;
}

export function formatAmount(session: Pick<CheckoutSession, 'order_amount' | 'pricing_currency'>): string {
  const num = parseFloat(session.order_amount);
  if (isNaN(num)) {
    return session.pricing_currency === 'INR'
      ? `₹${session.order_amount}`
      : `${session.pricing_currency} ${session.order_amount}`;
  }
  // Format to max 2 decimal places, strip only .00 (keep .50 as-is)
  const formatted = num.toFixed(2).replace(/\.00$/, '');
  return session.pricing_currency === 'INR'
    ? `₹${formatted}`
    : `${session.pricing_currency} ${formatted}`;
}

function CheckoutBackdrop({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,var(--checkout-bg-top)_0%,var(--checkout-bg-bottom)_72%,#ffffff_100%)] px-4 py-5 text-slate-900">
      <div className="mx-auto max-w-sm">
        <div className="relative overflow-hidden rounded-[34px] bg-white/35 px-5 pb-7 pt-5 shadow-[0_24px_80px_rgba(86,102,220,0.18)] backdrop-blur">
          <div className="pointer-events-none absolute inset-x-[-12%] top-0 h-64 rounded-b-[38px] bg-[linear-gradient(180deg,var(--checkout-accent)_0%,var(--checkout-accent-strong)_100%)]" />
          <div className="relative">
            <h1 className="text-center text-[2rem] font-extrabold tracking-tight text-white">
              {title}
            </h1>
            {children}
            {footer ? <div className="mt-5 text-center">{footer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroAmountCard({
  session,
  caption,
  showOrderNumber = true,
}: {
  session: CheckoutSession;
  caption: string;
  showOrderNumber?: boolean;
}) {
  return (
    <div className="mt-8 rounded-[28px] bg-white px-6 py-8 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
      <p className="text-[1.05rem] font-medium text-slate-700">{caption}</p>
      <p className="mt-3 text-[2.25rem] font-extrabold tracking-tight text-[var(--checkout-accent-strong)]">
        {formatAmount(session)}
      </p>
      {showOrderNumber ? (
        <p className="mt-3 text-sm font-medium text-slate-500">
          Order #{session.merchant_order_no}
        </p>
      ) : null}
    </div>
  );
}

function PrimaryAction({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mt-6 w-full rounded-full bg-[linear-gradient(135deg,var(--checkout-accent)_0%,var(--checkout-accent-strong)_100%)] px-6 py-4 text-xl font-bold text-white shadow-[0_16px_36px_rgba(91,106,255,0.28)] transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[var(--checkout-accent)] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
    >
      {label}
    </button>
  );
}

function SecondaryTextButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 text-sm font-semibold text-slate-600 underline decoration-slate-300 underline-offset-4 transition hover:text-slate-900"
    >
      {label}
    </button>
  );
}

function LoadingState() {
  return (
    <CheckoutBackdrop title="Payment">
      <div className="mt-10 rounded-[28px] bg-white px-6 py-12 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[var(--checkout-soft)] border-t-[var(--checkout-accent-strong)]" />
        <p className="mt-5 text-lg font-semibold text-slate-800">Loading checkout...</p>
      </div>
    </CheckoutBackdrop>
  );
}

function PaymentState({
  session,
  error,
  onPay,
  onCancel,
}: {
  session: CheckoutSession;
  error?: string | null;
  onPay: () => void;
  onCancel?: () => void;
}) {
  return (
    <CheckoutBackdrop
      title="Payment"
      footer={session.return_url && onCancel ? <SecondaryTextButton label="Return to Merchant" onClick={onCancel} /> : null}
    >
      <HeroAmountCard session={session} caption="The amount you need to pay" />
      {error ? (
        <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-center text-sm font-semibold leading-5 text-amber-800 ring-1 ring-amber-100">
          {error}
        </p>
      ) : null}
      <PrimaryAction label="Pay" onClick={onPay} />
    </CheckoutBackdrop>
  );
}

function CryptoTransferState({
  session,
  onReturnToMerchant,
}: {
  session: CheckoutSession;
  onReturnToMerchant: () => void;
}) {
  if (!session.crypto_transfer) {
    return <LoadingState />;
  }

  return (
    <CheckoutBackdrop
      title="Payment"
      footer={session.return_url ? <SecondaryTextButton label="Return to Merchant" onClick={onReturnToMerchant} /> : null}
    >
      <CryptoTransferCheckout transfer={session.crypto_transfer} />
    </CheckoutBackdrop>
  );
}

function UATSimulatorState({
  session,
  onSimulatePayment,
  onCancel,
}: {
  session: CheckoutSession;
  onSimulatePayment: (status: 'SUCCESS' | 'FAILED') => void | Promise<void>;
  onCancel?: () => void;
}) {
  return (
    <CheckoutBackdrop
      title="UAT Payment"
      footer={session.return_url && onCancel ? <SecondaryTextButton label="Return to Merchant" onClick={onCancel} /> : null}
    >
      <HeroAmountCard session={session} caption="Simulated payment amount" />
      <div className="mt-6 grid gap-3">
        <button
          type="button"
          onClick={() => void onSimulatePayment('SUCCESS')}
          className="w-full rounded-full bg-emerald-600 px-6 py-4 text-lg font-bold text-white shadow-[0_14px_32px_rgba(5,150,105,0.24)] transition hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
        >
          Simulate Payment Success
        </button>
        <button
          type="button"
          onClick={() => void onSimulatePayment('FAILED')}
          className="w-full rounded-full bg-red-600 px-6 py-4 text-lg font-bold text-white shadow-[0_14px_32px_rgba(220,38,38,0.20)] transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Simulate Payment Failure
        </button>
      </div>
    </CheckoutBackdrop>
  );
}

function SubmittingState() {
  return (
    <CheckoutBackdrop title="Payment">
      <div className="mt-10 rounded-[28px] bg-white px-6 py-12 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[var(--checkout-soft)] border-t-[var(--checkout-accent-strong)]" />
        <h2 className="mt-5 text-xl font-bold text-slate-900">Processing Payment</h2>
        <p className="mt-2 text-sm text-slate-600">Please wait while we process your payment...</p>
      </div>
    </CheckoutBackdrop>
  );
}

function WaitingState({
  session,
  onReturnToMerchant,
}: {
  session?: CheckoutSession | null;
  onReturnToMerchant?: () => void;
}) {
  return (
    <CheckoutBackdrop
      title="Payment"
      footer={session?.return_url && onReturnToMerchant ? <SecondaryTextButton label="Return to Merchant" onClick={onReturnToMerchant} /> : null}
    >
      <div className="mt-10 rounded-[28px] bg-white px-6 py-12 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[var(--checkout-soft)] border-t-emerald-500" />
        <h2 className="mt-5 text-xl font-bold text-slate-900">Redirecting to Payment</h2>
        <p className="mt-2 text-sm text-slate-600">You will be redirected to complete your payment...</p>
      </div>
    </CheckoutBackdrop>
  );
}

function StatusCard({
  tone,
  title,
  description,
  session,
  onReturnToMerchant,
}: {
  tone: 'success' | 'failure' | 'cancel' | 'expired' | 'invalid' | 'error';
  title: string;
  description: string;
  session?: CheckoutSession | null;
  onReturnToMerchant?: () => void;
}) {
  const toneMap: Record<'success' | 'failure' | 'cancel' | 'expired' | 'invalid' | 'error', { accent: string; bg: string; icon: ReactNode }> = {
    success: {
      accent: 'text-emerald-600',
      bg: 'bg-emerald-100',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />,
    },
    failure: {
      accent: 'text-red-600',
      bg: 'bg-red-100',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />,
    },
    cancel: {
      accent: 'text-amber-600',
      bg: 'bg-amber-100',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    expired: {
      accent: 'text-orange-600',
      bg: 'bg-orange-100',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    invalid: {
      accent: 'text-red-600',
      bg: 'bg-red-100',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />,
    },
    error: {
      accent: 'text-red-600',
      bg: 'bg-red-100',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
  };

  const activeTone = toneMap[tone];

  return (
    <CheckoutBackdrop
      title="Payment"
      footer={session?.return_url && onReturnToMerchant ? <SecondaryTextButton label="Return to Merchant" onClick={onReturnToMerchant} /> : null}
    >
      <div className="mt-10 rounded-[28px] bg-white px-6 py-10 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${activeTone.bg}`}>
          <svg className={`h-8 w-8 ${activeTone.accent}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {activeTone.icon}
          </svg>
        </div>
        <h2 className="mt-5 text-2xl font-bold text-slate-900">{title}</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
        {session ? (
          <div className="mt-6 rounded-[20px] bg-slate-50 px-4 py-4 text-left">
            <p className="text-sm text-slate-600">Order ID: {session.platform_order_id}</p>
            {tone === 'success' ? (
              <p className="mt-1 text-sm text-slate-600">Merchant Order: {session.merchant_order_no}</p>
            ) : null}
            {tone === 'success' && session.tx_hash ? (
              <p className="mt-1 break-all text-sm font-semibold text-slate-800">Tx Hash: {session.tx_hash}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </CheckoutBackdrop>
  );
}

const paymentMethodLogos: Record<string, { src: string; alt: string; className: string }> = {
  phonepe: {
    src: phonePeLogo,
    alt: 'PhonePe',
    className: 'h-11',
  },
  googlepay: {
    src: googlePayLogo,
    alt: 'Google Pay',
    className: 'h-10',
  },
  paytm: {
    src: paytmLogo,
    alt: 'Paytm',
    className: 'h-10',
  },
  upi: {
    src: upiLogo,
    alt: 'UPI',
    className: 'h-9',
  },
};

function PaymentOption({
  link,
  selected,
  onSelect,
}: {
  link: UPIAppLink;
  selected: boolean;
  onSelect: () => void;
}) {
  const logo = paymentMethodLogos[link.app] ?? paymentMethodLogos.upi;

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Select ${link.label}`}
      onClick={onSelect}
      className={`relative w-full rounded-[22px] border px-5 py-4 shadow-[0_12px_28px_rgba(84,98,198,0.10)] transition ${
        selected
          ? 'border-[#4a5cf0] bg-white ring-2 ring-[#a7b0ff]'
          : 'border-white/80 bg-white/95 hover:-translate-y-0.5 hover:border-[#c8d0ff]'
      }`}
    >
      <div className="flex items-center justify-center">
        <img src={logo.src} alt={logo.alt} className={`${logo.className} w-auto object-contain`} />
      </div>
      {selected ? (
        <span
          aria-hidden="true"
          className="absolute right-4 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--checkout-accent)] text-sm font-bold text-white"
        >
          ✓
        </span>
      ) : null}
    </button>
  );
}

function UPIChooserState({
  session,
  upiIntent,
  onPay,
  onReturnToMerchant,
}: {
  session: CheckoutSession;
  upiIntent: string;
  onPay: (targetUrl?: string) => void | Promise<void>;
  onReturnToMerchant: () => void;
}) {
  const appLinks = buildUPIAppLinks(upiIntent);
  const [selectedApp, setSelectedApp] = useState<string>('');
  const selectedLink = appLinks.find((link) => link.app === selectedApp);

  return (
    <CheckoutBackdrop
      title="Payment"
      footer={session.return_url ? <SecondaryTextButton label="Return to Merchant" onClick={onReturnToMerchant} /> : null}
    >
      <HeroAmountCard session={session} caption="The amount you need to pay" showOrderNumber={false} />

      <div className="mt-6 space-y-3">
        {appLinks.map((link) => (
          <PaymentOption
            key={link.app}
            link={link}
            selected={selectedApp === link.app}
            onSelect={() => setSelectedApp(link.app)}
          />
        ))}
      </div>

      <PrimaryAction
        label="Pay"
        disabled={!selectedLink}
        onClick={() => {
          if (!selectedLink) {
            return;
          }
          void onPay(selectedLink.url);
        }}
      />
    </CheckoutBackdrop>
  );
}

export function CheckoutStates({ state, session, error, upiIntent, onPay, onSimulatePayment, onReturnToMerchant, onCancel }: CheckoutStatesProps) {
  switch (state) {
    case 'loading':
      return <LoadingState />;
    case 'payment':
      return session ? <PaymentState session={session} error={error} onPay={() => void onPay()} onCancel={onCancel} /> : <LoadingState />;
    case 'crypto_transfer':
      return session ? (
        <CryptoTransferState session={session} onReturnToMerchant={onReturnToMerchant} />
      ) : <LoadingState />;
    case 'uat_simulator':
      return session && onSimulatePayment ? (
        <UATSimulatorState
          session={session}
          onSimulatePayment={onSimulatePayment}
          onCancel={onCancel}
        />
      ) : <LoadingState />;
    case 'submitting':
      return <SubmittingState />;
    case 'upi_chooser':
      return session && upiIntent ? (
        <UPIChooserState
          session={session}
          upiIntent={upiIntent}
          onPay={onPay}
          onReturnToMerchant={onReturnToMerchant}
        />
      ) : <LoadingState />;
    case 'waiting':
      return <WaitingState session={session} onReturnToMerchant={onReturnToMerchant} />;
    case 'success':
      return (
        <StatusCard
          tone="success"
          title="Payment Successful!"
          description={session ? `Your payment of ${formatAmount(session)} has been completed.` : 'Your payment has been completed.'}
          session={session}
          onReturnToMerchant={onReturnToMerchant}
        />
      );
    case 'failure':
      return (
        <StatusCard
          tone="failure"
          title="Payment Failed"
          description="Your payment could not be processed. Please try again or contact support."
          session={session}
          onReturnToMerchant={onReturnToMerchant}
        />
      );
    case 'cancel':
      return (
        <StatusCard
          tone="cancel"
          title="Payment Cancelled"
          description="You have cancelled the payment process."
          session={session}
          onReturnToMerchant={onReturnToMerchant}
        />
      );
    case 'expired':
      return (
        <StatusCard
          tone="expired"
          title="Checkout Expired"
          description="This checkout link has expired. Please request a new checkout link from the merchant."
          session={session}
          onReturnToMerchant={onReturnToMerchant}
        />
      );
    case 'invalid':
      return (
        <StatusCard
          tone="invalid"
          title="Invalid Checkout Link"
          description="This checkout link is not valid. Please check the link or contact the merchant."
          session={session}
          onReturnToMerchant={onReturnToMerchant}
        />
      );
    case 'error':
      return (
        <StatusCard
          tone="error"
          title="Something Went Wrong"
          description={error || 'An unknown error occurred'}
          session={session}
          onReturnToMerchant={onReturnToMerchant}
        />
      );
    default:
      return <LoadingState />;
  }
}
